import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useSearchParams } from "react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    CalendarIcon,
    UserIcon,
    ClockIcon,
    PencilIcon,
    PhoneIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { formatTime, formatDateTimeString } from "@/utils/format/date-time";
import useModal from "@/hooks/use-modal";
const AppointmentModal = lazy(() =>
    import("./appointment-modal").then((module) => ({
        default: module.AppointmentModal,
    })),
);
import { cn } from "@/utils/cn";

export const AppointmentCard = ({ appointment, upcoming }) => {
    const [searchParams] = useSearchParams();
    const appointmentGuid = searchParams.get("id");
    const tab = searchParams.get("tab");

    const [approvalState, setApprovalState] = useState();
    const appointmentModal = useModal();

    const onUpdateStatus = (id) => {
        appointmentModal.open();

        setApprovalState(id);
    };

    // Is appointment in URL
    const isTarget =
        String(appointment.appointmentGuid) === String(appointmentGuid);

    const ref = useRef(null);

    // Scroll to appointment in URL
    useEffect(() => {
        if (!isTarget || !ref.current) return;

        requestAnimationFrame(() => {
            ref.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        });
    }, [isTarget, tab, appointmentGuid]);

    return (
        <>
            <Card
                size="lg"
                id={`appointment-${appointment.appointmentGuid}`}
                ref={ref}
                className={cn(
                    isTarget ? "ring-2 ring-emerald-950/50" : "",
                    "grid-cols-12",
                )}
            >
                <div className="col-span-full flex flex-col gap-2 text-base/5 text-neutral-500 md:col-span-8">
                    <div className="flex flex-wrap content-start items-end gap-2">
                        <p className="text-xl font-semibold text-darkgreen-500">
                            {appointment.serviceTitle}
                        </p>
                        <Badge
                            variant={
                                appointment.statusID === 0 ||
                                appointment.statusID === 1
                                    ? "warning"
                                    : appointment.statusID === 2
                                      ? "success"
                                      : "danger"
                            }
                        >
                            {appointment.statusID === 0 ||
                            appointment.statusID === 1
                                ? "Waiting Approval"
                                : appointment.statusID === 2
                                  ? "Confirmed"
                                  : "Declined"}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                        <UserIcon className="size-5" />
                        <p>
                            {appointment.firstName + " " + appointment.lastName}
                        </p>
                    </div>
                    <div className="flex items-center gap-1">
                        <CalendarIcon className="size-5" />
                        <p>{formatDateTimeString(appointment.startDate)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                        <ClockIcon className="size-5" />
                        <p>{formatTime(appointment.startDate)}</p>
                    </div>
                    <div className="flex flex-col items-start gap-3 md:flex-row">
                        <div className="flex items-center gap-1">
                            <PhoneIcon className="size-5" />
                            <p>
                                <a
                                    href={`tel:${appointment.phoneNumber}`}
                                    className="hover:underline"
                                >
                                    {appointment.phoneNumber}
                                </a>
                            </p>
                        </div>
                        <div className="flex items-center gap-1">
                            <EnvelopeIcon className="size-5" />
                            <p>
                                <a
                                    href={`mailto:${appointment.email}`}
                                    className="hover:underline"
                                >
                                    {appointment.email}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
                <div className="col-span-full ms-auto mt-auto flex flex-wrap justify-end gap-2 md:col-span-4">
                    {upcoming && appointment.statusID == 0 && (
                        <Button
                            type="button"
                            variant="success"
                            size="sm"
                            onClick={() => onUpdateStatus(2)}
                        >
                            <CheckIcon />
                            Approve
                        </Button>
                    )}
                    {upcoming &&
                        (appointment.statusID == 0 ||
                            appointment.statusID == 1) && (
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => onUpdateStatus(1)}
                            >
                                <PencilIcon />
                                Amend
                            </Button>
                        )}
                    {upcoming && appointment.statusID == 0 && (
                        <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => onUpdateStatus(3)}
                        >
                            <XMarkIcon />
                            Decline
                        </Button>
                    )}
                    {upcoming &&
                        (appointment.statusID == 1 ||
                            appointment.statusID == 2) && (
                            <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={() => onUpdateStatus(4)}
                            >
                                <XMarkIcon />
                                Cancel
                            </Button>
                        )}
                </div>
            </Card>
            {upcoming && (
                <Suspense fallback={null}>
                    <AppointmentModal
                        isOpen={appointmentModal.isOpen}
                        onClose={() => appointmentModal.close()}
                        appointmentGuid={appointment.appointmentGuid}
                        serviceGuid={appointment.serviceGuid}
                        title={appointment.serviceTitle}
                        date={appointment.startDate}
                        statusId={approvalState}
                    />
                </Suspense>
            )}
        </>
    );
};
